import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import { HStack, IconButton, Text } from '@chakra-ui/react';
import { getMaxFlexiDays } from '#constants';
import { useEffect } from 'react';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';

/**
 * Symmetric flexi days stepper: Flexible: [−] N [+] days
 * Caps at ±2 for pay-as-you-go and ±15 for subscribers.
 */
export default function FlexiDaysControl({
  editMode = false,
  value: controlledValue,
  onChange,
  size = 'sm',
}) {
  const { setFlexiDays, setPlanSlug, flexiDaysAfter, sub, planSlug } = useUIStore(
    (state) => ({
      setFlexiDays: state.setFlexiDays,
      setPlanSlug: state.setPlanSlug,
      flexiDaysAfter: state?.trackerInput?.flexiDaysAfter ?? 0,
      sub: state.sub,
      planSlug: state.planSlug,
    }),
    shallow
  );

  const isControlled = controlledValue != null && typeof onChange === 'function';
  const maxFlexi = getMaxFlexiDays({ sub, planSlug, editMode });
  const raw = isControlled ? controlledValue : flexiDaysAfter;
  const value = Math.min(Math.max(0, Number(raw) || 0), maxFlexi);

  useEffect(() => {
    if (isControlled) {
      if ((controlledValue ?? 0) > maxFlexi) onChange(maxFlexi);
      return;
    }
    if ((flexiDaysAfter ?? 0) <= maxFlexi) return;
    // PAYG: go through setPlanSlug so the wider window is remembered.
    // Anything else: clamp only — restoring here can loop in edit mode.
    if (planSlug === 'pay-as-you-go') setPlanSlug(planSlug);
    else setFlexiDays(maxFlexi);
  }, [
    maxFlexi,
    flexiDaysAfter,
    controlledValue,
    isControlled,
    onChange,
    setPlanSlug,
    setFlexiDays,
    planSlug,
  ]);

  const setValue = (next) => {
    const capped = Math.min(Math.max(0, next), maxFlexi);
    if (isControlled) onChange(capped);
    else setFlexiDays(capped);
  };

  const btnSize = size === 'xs' ? 'xs' : 'xs';
  const fontSize = size === 'xs' ? 'sm' : 'sm';

  return (
    <HStack spacing={2} color="orange.500" fontSize={fontSize} fontWeight={500}>
      <Text whiteSpace="nowrap">Flexible:</Text>
      <HStack spacing={1}>
        <IconButton
          aria-label="Decrease flexible days"
          icon={<MinusIcon boxSize={2.5} />}
          size={btnSize}
          variant="outline"
          colorScheme="orange"
          borderRadius="md"
          isDisabled={value <= 0}
          onClick={(e) => {
            e.stopPropagation();
            setValue(value - 1);
          }}
        />
        <Text
          minW="1.75rem"
          textAlign="center"
          fontWeight={600}
          color="gray.800"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </Text>
        <IconButton
          aria-label="Increase flexible days"
          icon={<AddIcon boxSize={2.5} />}
          size={btnSize}
          variant="outline"
          colorScheme="orange"
          borderRadius="md"
          isDisabled={value >= maxFlexi}
          onClick={(e) => {
            e.stopPropagation();
            setValue(value + 1);
          }}
        />
      </HStack>
      <Text whiteSpace="nowrap" color="orange.500">
        day{value === 1 ? '' : 's'}
      </Text>
    </HStack>
  );
}
